// @flow
import type {Node} from 'react';

import {useMountEffect} from '@react-hookz/web';
import {initSplit} from '@travel-frontend/split-io';
import {fetchUserId, isUserIdSet} from '@travel-frontend/user-id';
import Cookies from 'js-cookie';
import {parseInt} from 'lodash';
import {lazy, Suspense, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Route,
  Routes as RouterRoutes,
  useLocation,
  useMatch,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';

import {config} from 'config';
import {PAGE_CHECKOUT, PAGE_CONFIRM} from 'consts';
import {useTracking} from 'hooks/useTracking';
import {useUpsellingQueryParams} from 'hooks/useUpsellingQueryParams';
import {PageLoading} from 'modules/CheckoutPage/components/PageLoading';
import {NoMatch} from 'modules/Common/NoMatch';
import {RedirectToPortal} from 'modules/Common/NoMatch/RedirectToPortal';
import {PageLoading as ConfirmationLoading} from 'modules/ConfirmationPage/components/PageLoading';
import {setJourneyParams} from 'redux/uiState/layout/booking/actions';
import {getBookingInProcess} from 'redux/uiState/layout/booking/reducer';
import {fetchJourney} from 'redux/v1/actions';
import {getBookStatus} from 'redux/v1/bookStatus/actions';
import {isInProgress} from 'redux/v1/bookStatus/reducer';
import {isJourneyLoaded} from 'redux/v1/journey/reducer';
import {isValid} from 'utils';

// represents router resources
type Resources = {
  CHECKOUT: string,
  CONFIRMATION: string,
  PORTAL_URL: string,
};

export const resources: Resources = {
  CHECKOUT: '/:journeyId',
  CONFIRMATION: '/confirmation/:journeyId',
  PORTAL_URL: config.portalUrl,
};

export const Routes = (): Node => {
  const {trackPageLoad, trackPageView} = useTracking();
  const journeyLoaded: boolean = useSelector(isJourneyLoaded);
  const userIdLoaded: boolean = useSelector(isUserIdSet);
  const isReadyToTrack: boolean = journeyLoaded && userIdLoaded;
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const dispatch = useDispatch();
  const location = useLocation();
  const confirmationPageMatch = useMatch(resources.CONFIRMATION);

  const bookingInProgress = useSelector(isInProgress);
  const bookingInProcess = useSelector(getBookingInProcess);
  const currentPage = confirmationPageMatch ? PAGE_CONFIRM : PAGE_CHECKOUT;
  const checkoutLoading =
    (bookingInProcess || bookingInProgress) && currentPage === PAGE_CHECKOUT;
  const [isTracked, setIsTracked] = useState(false);

  const pathParts = location.pathname.split('/').slice(1);
  const upsellingQueryParams = useUpsellingQueryParams();
  const journeyId =
    pathParts[0] === 'confirmation' ? pathParts[1] : pathParts[0];

  useEffect(() => {
    if (isReadyToTrack) {
      if (navigationType === 'POP' && !isTracked) {
        trackPageLoad(currentPage, checkoutLoading);
        setIsTracked(true);
      } else if (navigationType === 'REPLACE' || navigationType === 'PUSH') {
        trackPageView(currentPage);
      }
    }
  }, [
    trackPageLoad,
    isTracked,
    trackPageView,
    isReadyToTrack,
    navigationType,
    checkoutLoading,
    currentPage,
  ]);

  useMountEffect(() => {
    if (journeyId) {
      const token = Cookies.get('token');

      if (!isValid(token)) {
        navigate('/');
      }
      const parsedJourneyId = parseInt(journeyId);
      dispatch(setJourneyParams(parsedJourneyId, token));
      dispatch(getBookStatus(parsedJourneyId, token)).then(
        () =>
          dispatch(fetchJourney(parsedJourneyId, token, upsellingQueryParams)) // add queryparams here
      );
    }

    dispatch(initSplit(config.splitApiKey, config.splitApiSettings));
    dispatch(fetchUserId());
  });

  const preloadComponent = (component) => {
    const Component = lazy(component);
    component();
    return Component;
  };

  const CheckoutPage = preloadComponent(() => import('modules/CheckoutPage'));
  const ConfirmationPage = lazy(() => import('modules/ConfirmationPage'));

  return (
    <Suspense fallback={<PageLoading />}>
      <RouterRoutes>
        <Route path={resources.CHECKOUT} element={<CheckoutPage />} />
        <Route
          path={resources.CONFIRMATION}
          element={
            <Suspense fallback={<ConfirmationLoading />}>
              <ConfirmationPage />
            </Suspense>
          }
        />
        <Route path="*" element={<NoMatch to={resources.PORTAL_URL} />} />
        {process.env.STAGE === 'mock' && (
          <Route path="/mock/portal" element={<RedirectToPortal />} />
        )}
      </RouterRoutes>
    </Suspense>
  );
};
